import { clsx } from "clsx";
import type Konva from "konva";
import { type CSSProperties, type FC, memo, useMemo, useRef } from "react";
import { useHover } from "react-aria";
import { shallowEqual, useDispatch } from "react-redux";

import styleTrack from "../styles/Track.module.scss";

import { type TRACK_TOOLTIP_TYPE } from "../../../app-utils/track-shapes/shape-feature.js";
import { type IPileup } from "../../../app-utils/track-shapes/track-pileup-factory.js";
import { type Dictionary } from "../../../app-utils/type.js";
import { type Group, type InputTableQuerySpec } from "../../../graphql.js";
import {
  type ISortableTrack,
  getTableSpecFromTrackUsingGenomeInformation,
} from "../../../redux/modules/genome-browser.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import { type SetTooltipContent } from "../GenomeViewerTracksList.js";
import { useDrawFeatures } from "../hooks/useDrawFeatures.js";
import { FeatureTrackHeader } from "./FeatureTrackHeader.js";

export type IConditionNameLookup = Record<string, string>; // map by condition

export interface IFeatureTrackProps {
  track: ISortableTrack;
  trackHeight: number;
  tableQuerySpec?: InputTableQuerySpec; // for the interactive highlighting of features, injected
  pileup: IPileup;
  expressionValuesOverflow?: boolean;
  overflow: boolean;
  empty: boolean;
  showAllFeatures: boolean;
  featureColor?: string; // in case a specific color is provided e.g. in a subtrack like bed track
  trackMessage?: string;

  communityAnnotation?: Dictionary<Group[]>;
  communityAnnotationOverflow?: boolean;
  setTooltipContent: SetTooltipContent;
}

export type CallTooltip = (
  interactiveShape: Konva.Rect,
  targetShape: Konva.Rect,
  tooltipState: Partial<TooltipState>,
) => void;

export interface TooltipState {
  style: Pick<CSSProperties, "left" | "top" | "width" | "height">;
  isLoading?: boolean;
  uid: string; // necessary for the tooltip to update correctly
  type?: TRACK_TOOLTIP_TYPE;
  title?: string;
  getContent?: () => Promise<any>;
  content?: any;
  shapeHovered?: boolean;
}

/**
 * TODO
 *   - Cache shapes but features processing needs to be larger than the current visible range
 *     - test performance when processing the whole range at a given zoom level.
 *   - loading / rendering
 */

/**
 * A visualization component that renders a specific genome browser track.
 *
 * The component is expected to be used within an existing SVG context and does NOT create its own SVG element. The
 * main input data is a pre-computed pileup of genomic features. During the pileup calculation each genomic feature is
 * assigned view coordinates and a width such that the features don't overlap on the screen.
 */
const FeatureTrack: FC<IFeatureTrackProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const ref = useRef<HTMLDivElement>(null);

  const { hoverProps, isHovered } = useHover({});

  const tableQuerySpec = useMemo(
    () => dispatch(getTableSpecFromTrackUsingGenomeInformation(props.track)),
    [props.track],
  );
  const { messageOnTopMargin } = useDrawFeatures({ ...props, tableQuerySpec }, ref);

  const pileup = props.pileup;

  return (
    <div className={clsx(styleTrack.track, { [styleTrack.track_hovered]: isHovered })} {...hoverProps}>
      <FeatureTrackHeader
        track={props.track}
        overflow={props.overflow}
        pileup={pileup}
        showAllFeatures={props.showAllFeatures}
        messageOnTopMargin={messageOnTopMargin}
        isTrackHovered={isHovered}
      />
      <div
        className={clsx(styleTrack.container, styleTrack.container_no_top, styleTrack.container_no_bottom)}
        style={{
          marginTop: pileup?.pileupParams.hideLabels === true ? pileup?.pileupParams.labelHeight : 0,
        }}
      >
        <div ref={ref} />
      </div>
    </div>
  );
};

const MemoFeatureTrack = memo(FeatureTrack, shallowEqual);
export { MemoFeatureTrack as FeatureTrack };
